/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import { Table, TableBody, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { authSetIsLoggedIn, appSetNotification } from '@/store';
import { NotificationTypeEnum, ISubscriptionsTable, OtpList } from '@/types';
import { Loader } from '@/components';
import { adminProvider } from '@/providers';
import moment from 'moment';

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'weight',
})<{ color?: string; weight?: number }>(({ color, weight }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'white',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: '0px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: color,
    fontWeight: weight,
    maxWidth: '300px',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#F9FCFF',
  },
}));

const OTPTable: ISubscriptionsTable = {
  header: [
    'Email',
    'Phone number',
    'OTP code',
    'Kind',
    // 'User Id',
    'Created',
    'Valid Until',
    // 'Attempts',
  ],
  body: [
    'email',
    'phone',
    'code',
    'kind',
    // 'userId',
    'createdAt',
    'validUntil',
    // 'attempts',
  ],
};

export const OtpTable = ({
  search,
  setUpdateOtpTable,
  updateOtpTable,
}: {
  search: string;
  setUpdateOtpTable: (value: boolean) => void;
  updateOtpTable: boolean;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpList, setOtpList] = useState<OtpList[]>();
  const dispatch = useDispatch();

  const getOtpList = async () => {
    setIsLoading(true);
    const payload = {
      limit: 50,
      offset: 0,
      value: search,
    };
    const { ok, data } = await adminProvider.getOtpCode(payload);
    if (ok && data) {
      const { items } = data;
      const newItems = items.map((item: OtpList) => {
        const createdAt = item.createdAt ? moment(item.createdAt).format('DD.MM.YYYY HH:mm') : null;
        const validUntil = item.validUntil
          ? moment(item.validUntil).format('DD.MM.YYYY HH:mm')
          : null;
        const [email, phone] = item.value.split(',');
        return { ...item, createdAt, validUntil, email, phone };
      });
      setOtpList(newItems as OtpList[]);
      setUpdateOtpTable(false);
    } else {
      dispatch(authSetIsLoggedIn(false));
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          'Your access token is expired, please login again'
        )
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getOtpList();
  }, [search]);

  useEffect(() => {
    if (updateOtpTable) {
      getOtpList();
    }
  }, [updateOtpTable]);

  const getCellProperties = (
    row: OtpList,
    body: string
  ): { cell: string | number | null; color: string; weight: number } => {
    const cell = (row as OtpList)[body as keyof OtpList];
    const isCode = body === 'code';
    const color = isCode ? 'red' : '#2A3842';
    const weight = isCode ? 600 : 400;
    return { cell, color, weight };
  };

  if (isLoading) return <Loader />;

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 350 }}>
        <TableHead>
          <TableRow>
            {OTPTable.header.map((header: string) => {
              return <StyledTableCell key={header}>{header}</StyledTableCell>;
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {otpList?.map((row: OtpList, index) => (
            <StyledTableRow key={index}>
              {OTPTable.body.map((body: string, bodyIndex: number) => {
                const { cell, color, weight } = getCellProperties(row, body);
                return (
                  <StyledTableCell
                    key={bodyIndex}
                    component='th'
                    scope='row'
                    color={color}
                    weight={weight}
                  >
                    {cell}
                  </StyledTableCell>
                );
              })}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
